import { useState } from "preact/hooks";
import ChevronUpIcon from "~/components/icons/chevron-up-icon.tsx";
import type { ModalProps } from "~/components/modal.tsx";
import Modal from "~/components/modal.tsx";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import classes from "./faqs-modal.module.css";

const content = [
	{
		question: "What is Pix-a-pic?",
		answer: (
			<>
				<div>
					Pix-a-pic is a creative and hands-on way to turn your favourite photos
					into custom brick mosaics you can build yourself. It's perfect as a
					thoughtful gift or a fun activity to share with family and friends.
				</div>
				<div>
					The best part? Each Pix-a-pic kit is reusable. You can rebuild it with
					new photos as many times as you like, with no extra cost. Whether for
					kids or adults, Pix-a-pic offers a unique mix of creativity, quality
					time, and a finished piece you’ll be proud to display.
				</div>
				<div>
					The base kit includes monochromatic bricks in shades from black to
					white (with 5 greys in between), but you can also switch to fully
					colored bricks at any point by ordering the required extra bricks!
				</div>
			</>
		),
	},
	{
		question: "How do I get my Pix-a-pic instructions after purchasing it?",
		answer: (
			<>
				<div>
					Your Pix-a-pic kit includes a unique code inside the instruction
					manual. You’ll use this code on the Pix-a-pic website to start the
					design process and generate the assembly instructions for your brick
					mosaic.
				</div>
				<div>
					If you’ve purchased multiple kits, you just need to input one code to
					unlock larger mosaic sizes.
				</div>
				<div>
					Instructions will be sent to your email, so please double-check that
					you've entered the correct address.
				</div>
			</>
		),
	},
	{
		question: "What can I do if I did not get an email with my instructions?",
		answer: (
			<>
				<div>
					If you don’t see the email, be sure to check your spam or junk folder.
					If it’s still missing, try generating the instructions again — and if
					that doesn’t work, feel free to contact us at{" "}
					<a href="mailto:pixapic@brick.me" target="_blank">
						<strong>pixapic@brick.me</strong>
					</a>{" "}
					and we’ll be happy to help!
				</div>
			</>
		),
	},
	{
		question: "How long does it take to build a Pix-a-pic brick mosaic kit?",
		answer: (
			<>
				<div>
					A single Pix-a-pic kit includes 4 baseplates, which combine to form
					your full brick mosaic. On average, each baseplate takes about 40–45
					minutes to complete. The entire build (if using one Pix-a-pic kit)
					typically takes around 3 hours. Of course, the more people you have
					helping, the faster it’ll come together!
				</div>
			</>
		),
	},
	{
		question: "What if I lose some parts while building my Pix-a-pic?",
		answer: (
			<>
				<div>
					We include spare bricks of every color, but should you need additional
					ones because you lost some pieces, just reach out to us at{" "}
					<a href="mailto:pixapic@brick.me" target="_blank">
						<strong>pixapic@brick.me</strong>
					</a>{" "}
					and we’ll help you out!
				</div>
			</>
		),
	},
	{
		question: "Are the photos I upload to the Pix-a-pic app saved on your end?",
		answer: (
			<>
				<div>
					Your images are uploaded to our server while you work on your design,
					but they are automatically deleted after your instructions are
					generated and emailed to you, or after you exit the app.
				</div>
			</>
		),
	},
	{
		question: "Is there a minimum age to build a Pix-a-pic brick mosaic?",
		answer: (
			<>
				<div>
					While the recommended age is 14 and up, younger children can
					definitely join in with adult supervision. Building a Pix-a-pic mosaic
					can be a fun and rewarding activity to do together! Just note that the
					small bricks are not suitable for children under 3 years old due to
					choking hazards.
				</div>
			</>
		),
	},
	{
		question: "What happens if I don’t like the image?",
		answer: (
			<>
				<div>
					Don't worry! You will be able to preview your design online before
					generating the instructions, and there's no limit to how many times
					you can generate a new set of instructions.
				</div>
			</>
		),
	},
	{
		question: "How do I choose a photo to upload?",
		answer: (
			<>
				<div>
					You can use any image from your phone, camera, or even a scanned
					print—whatever holds the most meaning for you.
				</div>
				<div>
					For the best results, choose a high-resolution photo with minimal fine
					details. Portraits with clearly visible faces tend to work especially
					well. If you want more than one subject to appear clearly, we
					recommend combining more Pix-a-pic kits to increase the overall mosaic
					size!
				</div>
				<div>
					Once uploaded, you'll be able to choose a size, zoom in, and adjust
					image settings before generating your instructions.
				</div>
			</>
		),
	},
	{
		question: "I have a question that is not covered in this FAQ",
		answer: (
			<>
				<div>
					Please feel free to ask us any other questions at{" "}
					<a href="mailto:pixapic@brick.me" target="_blank">
						<strong>pixapic@brick.me</strong>
					</a>
					.
				</div>
			</>
		),
	},
];

type FaqsModalProps = Pick<ModalProps, "id">;

function FaqsModal({ id }: FaqsModalProps) {
	const [activeIndex, setActiveIndex] = useState<number | undefined>();
	const t = useTranslator();
	return (
		<Modal id={id} size="large" bodyClassName={classes["faqs-body"]}>
			<h2 className="heading-1">{t("FAQs")}</h2>
			<div>
				{content.map((c, i) => (
					<details
						open={activeIndex === i}
						onToggle={(e) =>
							setActiveIndex((p) => {
								if (e.currentTarget.open) {
									return i;
								}
								if (p === i) {
									return undefined;
								}
								return p;
							})
						}
					>
						<summary>
							{c.question} <ChevronUpIcon />
						</summary>
						<div className={classes["answer"]}>{c.answer}</div>
					</details>
				))}
			</div>
		</Modal>
	);
}

export default FaqsModal;
